import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { portfolioApi, analysisApi, metadataApi } from '../api/client';
import toast from 'react-hot-toast';

const PortfolioContext = createContext(null);

export function usePortfolio() {
  const context = useContext(PortfolioContext);
  if (!context) {
    throw new Error('usePortfolio must be used within PortfolioProvider');
  }
  return context;
}

export function PortfolioProvider({ children }) {
  const [portfolio, setPortfolio] = useState(null);
  const [trades, setTrades] = useState([]);
  const [dividends, setDividends] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [analysis, setAnalysis] = useState(null);           // open positions
  const [analysisClosed, setAnalysisClosed] = useState(null); // closed positions — separate slot so refreshAll('open') doesn't clobber it
  const [pnlCurve, setPnlCurve] = useState(null);
  const [cashHistory, setCashHistory] = useState(null);
  const [sectors, setSectors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const loadPortfolio = useCallback(async () => {
    try {
      const data = await portfolioApi.getPortfolio();
      setPortfolio(data);
      return data;
    } catch (error) {
      console.error('Failed to load portfolio:', error);
      return null;
    }
  }, []);

  const loadTrades = useCallback(async () => {
    try {
      const data = await portfolioApi.getTrades();
      setTrades(data.trades || []);
      return data;
    } catch (error) {
      console.error('Failed to load trades:', error);
      return null;
    }
  }, []);

  const loadDividends = useCallback(async () => {
    try {
      const data = await portfolioApi.getDividends();
      setDividends(data.dividends || []);
      return data;
    } catch (error) {
      console.error('Failed to load dividends:', error);
      return null;
    }
  }, []);

  const loadDeposits = useCallback(async () => {
    try {
      const data = await portfolioApi.getDeposits();
      setDeposits(data.deposits || []);
      return data;
    } catch (error) {
      console.error('Failed to load deposits:', error);
      return null;
    }
  }, []);

  const loadAnalysis = useCallback(async (positionType = 'open') => {
    try {
      const data = await analysisApi.getSummary(positionType);
      if (positionType === 'closed') {
        setAnalysisClosed(data);
      } else {
        setAnalysis(data);
      }
      return data;
    } catch (error) {
      console.error('Failed to load analysis:', error);
      return null;
    }
  }, []);

  const loadPnlCurve = useCallback(async (basis = 'REALIZED_PLUS_DIVIDENDS', benchmark = 'NONE') => {
    try {
      const data = await analysisApi.getPnlCurve(basis, benchmark, true);
      setPnlCurve(data);
      return data;
    } catch (error) {
      console.error('Failed to load PnL curve:', error);
      return null;
    }
  }, []);

  const loadCashHistory = useCallback(async () => {
    try {
      const data = await analysisApi.getCashHistory();
      setCashHistory(data);
      return data;
    } catch (error) {
      console.error('Failed to load cash history:', error);
      return null;
    }
  }, []);

  const loadSectors = useCallback(async (holdings) => {
    if (!holdings || holdings.length === 0) return;
    
    try {
      const tickers = holdings
        .map(h => h.ticker?.toUpperCase())
        .filter(Boolean)
        .slice(0, 25);
      
      if (tickers.length === 0) return;
      
      const data = await metadataApi.getSectors(tickers);
      
      const sectorMap = {};
      data.items?.forEach(item => {
        if (item.ticker && item.sector) {
          sectorMap[item.ticker.toUpperCase()] = item.sector;
        }
      });
      
      setSectors(sectorMap);
      return sectorMap;
    } catch (error) {
      console.error('Failed to load sectors:', error);
      return null;
    }
  }, []);

  const refreshAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const [portfolioData] = await Promise.all([
        loadPortfolio(),
        loadTrades(),
        loadDividends(),
        loadDeposits(),
        loadAnalysis('open'),
        loadAnalysis('closed'), // prefetch so the Closed Positions tab renders instantly
        loadCashHistory(),
      ]);

      if (portfolioData?.holdings) {
        await loadSectors(portfolioData.holdings);
      }

      // Fire-and-forget: persist today's equity snapshot so max drawdown and
      // the equity curve have a daily history to work from. The endpoint is
      // idempotent (UPSERT on user_id+date) so safe to call on every refresh.
      analysisApi.saveSnapshot().catch(() => {});

      setLastUpdate(new Date());
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setIsLoading(false);
    }
  }, [loadPortfolio, loadTrades, loadDividends, loadDeposits, loadAnalysis, loadCashHistory, loadSectors]);

  // Helper to invalidate chart data and refresh all
  const invalidateAndRefresh = useCallback(async () => {
    // Invalidate chart data to force reload
    setPnlCurve(null);
    setCashHistory(null);
    // Refresh all other data
    await refreshAll();
  }, [refreshAll]);

  // CRUD Operations
  const createTrade = async (data) => {
    try {
      await portfolioApi.createTrade(data);
      toast.success('Trade created');
      // Invalidate charts and refresh all data
      invalidateAndRefresh();
      return true;
    } catch (error) {
      toast.error(error.message || 'Failed to create trade');
      return false;
    }
  };

  const updateTrade = async (id, data) => {
    try {
      await portfolioApi.updateTrade(id, data);
      toast.success('Trade updated');
      // Invalidate charts and refresh all data
      invalidateAndRefresh();
      return true;
    } catch (error) {
      toast.error(error.message || 'Failed to update trade');
      return false;
    }
  };

  const deleteTrade = async (id) => {
    try {
      await portfolioApi.deleteTrade(id);
      toast.success('Trade deleted');
      // Invalidate charts and refresh all data
      await invalidateAndRefresh();
      return true;
    } catch (error) {
      toast.error(error.message || 'Failed to delete trade');
      return false;
    }
  };

  const createDividend = async (data) => {
    try {
      await portfolioApi.createDividend(data);
      toast.success('Dividend recorded');
      // Invalidate charts and refresh all data
      invalidateAndRefresh();
      return true;
    } catch (error) {
      toast.error(error.message || 'Failed to create dividend');
      return false;
    }
  };

  const updateDividend = async (id, data) => {
    try {
      await portfolioApi.updateDividend(id, data);
      toast.success('Dividend updated');
      // Invalidate charts and refresh all data
      invalidateAndRefresh();
      return true;
    } catch (error) {
      toast.error(error.message || 'Failed to update dividend');
      return false;
    }
  };

  const deleteDividend = async (id) => {
    try {
      await portfolioApi.deleteDividend(id);
      toast.success('Dividend deleted');
      // Invalidate charts and refresh all data
      await invalidateAndRefresh();
      return true;
    } catch (error) {
      toast.error(error.message || 'Failed to delete dividend');
      return false;
    }
  };

  const createDeposit = async (data) => {
    try {
      await portfolioApi.createDeposit(data);
      toast.success('Deposit recorded');
      // Invalidate charts and refresh all data
      invalidateAndRefresh();
      return true;
    } catch (error) {
      toast.error(error.message || 'Failed to create deposit');
      return false;
    }
  };

  const updateDeposit = async (id, data) => {
    try {
      await portfolioApi.updateDeposit(id, data);
      toast.success('Deposit updated');
      // Invalidate charts and refresh all data
      invalidateAndRefresh();
      return true;
    } catch (error) {
      toast.error(error.message || 'Failed to update deposit');
      return false;
    }
  };

  const deleteDeposit = async (id) => {
    try {
      await portfolioApi.deleteDeposit(id);
      toast.success('Deposit deleted');
      // Invalidate charts and refresh all data
      await invalidateAndRefresh();
      return true;
    } catch (error) {
      toast.error(error.message || 'Failed to delete deposit');
      return false;
    }
  };

  // Computed values
  const summary = portfolio?.summary || {};
  const holdings = portfolio?.holdings || [];
  const realized = portfolio?.realized || {};

  const value = {
    // Data
    portfolio,
    summary,
    holdings,
    trades,
    dividends,
    deposits,
    analysis,
    analysisClosed,
    pnlCurve,
    cashHistory,
    sectors,
    realized,
    
    // State
    isLoading,
    lastUpdate,
    
    // Actions
    refreshAll,
    invalidateAndRefresh,
    loadPortfolio,
    loadTrades,
    loadDividends,
    loadDeposits,
    loadAnalysis,
    loadPnlCurve,
    loadCashHistory,
    
    // CRUD
    createTrade,
    updateTrade,
    deleteTrade,
    createDividend,
    updateDividend,
    deleteDividend,
    createDeposit,
    updateDeposit,
    deleteDeposit,
  };

  return (
    <PortfolioContext.Provider value={value}>
      {children}
    </PortfolioContext.Provider>
  );
}
